import { LoadingButton } from '@mui/lab'
import {
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@mui/material'
import { GreenText, LabelField } from 'components'
import * as controllers from 'controllers'
import { VehicleDto } from 'dtos'
import { ClusterShipperDto } from 'dtos/clusterShipperDto'
import { Formik } from 'formik'
import { useLoadingState } from 'hooks'
import { DEBOUNCE_DELAY, Junction } from 'localConstants'
import { VehiclesParameters } from 'parameters'
import { useEffect, useState } from 'react'
import { useDebounce } from 'utils'

interface ClusterShippersManageVehiclesAddVehiclesDialogProps {
  clusterShipper: ClusterShipperDto
  isLoading: boolean
  open: boolean
  onClose: () => void
  onSave: (values: any) => void
}

export default function ClusterShippersManageVehiclesAddVehiclesDialog({
  clusterShipper,
  isLoading,
  open,
  onClose,
  onSave
}: ClusterShippersManageVehiclesAddVehiclesDialogProps) {
  const [loadingState, setLoadingState] = useLoadingState({
    isGettingVehicles: false
  })

  const [vehicles, setVehicles] = useState<VehicleDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)

  const [parameters, setParameters] = useState<VehiclesParameters>({
    page: 0,
    pageSize: 10,
    order: 'asc',
    orderBy: 'vin'
  })

  const getVehicles = useDebounce((parameters: VehiclesParameters) => {
    setLoadingState('isGettingVehicles', true)
    controllers
      .getVehicles(parameters)
      .then(response => {
        setVehicles(response.value)
        setTotalCount(response.totalCount ?? 0)
      })
      .finally(() => {
        setLoadingState('isGettingVehicles', false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    if (open && clusterShipper) {
      setParameters({
        page: 0,
        pageSize: 10,
        order: 'asc',
        orderBy: 'vin',
        clusterShipperGuid: clusterShipper.clusterShipperGuid,
        statuses: ['isActive', 'hasNoClusterShipper', 'hasNoOutboundTrip'],
        junction: Junction.AND
      })
    }
  }, [open, clusterShipper])

  useEffect(() => {
    getVehicles(parameters)
  }, [getVehicles, parameters])

  return (
    <Dialog maxWidth='md' open={open}>
      <Formik
        enableReinitialize
        initialValues={[] as VehicleDto[]}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
        validateOnBlur={true}
        validateOnChange={true}
      >
        {({ handleSubmit, setValues, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='flex-start'>
                  <DialogTitle>Add Vehicles to Cluster Shipper</DialogTitle>

                  <Grid item xs={12}>
                    <LabelField label='Cluster Shipper#' value={clusterShipper.clusterShipperNumber} />
                  </Grid>
                  <Grid item xs={12}>
                    <LabelField label='Cluster Conversion Company' value={clusterShipper.clusterConversionCompany?.name} />
                  </Grid>
                  <Grid item xs={12}>
                    <LabelField label='Exporter' value={clusterShipper.exporterCompany?.name} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Buyer' value={clusterShipper.buyerCompany?.name} />
                  </Grid>

                  {loadingState.isGettingVehicles && (
                    <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                      <Grid>
                        <CircularProgress color='primary' size={50} />
                      </Grid>
                    </Grid>
                  )}

                  {!loadingState.isGettingVehicles && (
                    <Grid item xs={12}>
                      <Card variant='outlined' sx={{ width: '100%' }}>
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell padding='checkbox'>
                                  <Checkbox
                                    color='primary'
                                    checked={values.length === totalCount && values.length !== 0}
                                    disabled={isLoading || values.length === 0}
                                    indeterminate={values.length > 0 && values.length !== totalCount}
                                    onChange={() => {
                                      if (values.length === totalCount) {
                                        setValues([])
                                      } else {
                                        setValues(vehicles)
                                      }
                                    }}
                                  />
                                </TableCell>

                                <TableCell
                                  sx={theme => ({ color: theme.palette.primary.main })}
                                  sortDirection={parameters.orderBy === 'vin' ? parameters.order : false}
                                >
                                  <TableSortLabel
                                    active={parameters.orderBy === 'vin'}
                                    direction={parameters.orderBy === 'vin' ? parameters.order : undefined}
                                    onClick={() => {
                                      setParameters({
                                        ...parameters,
                                        order: parameters.orderBy === 'vin' && parameters.order === 'asc' ? 'desc' : 'asc',
                                        orderBy: 'vin'
                                      })
                                    }}
                                  >
                                    VIN
                                  </TableSortLabel>
                                </TableCell>

                                <TableCell
                                  sx={theme => ({ color: theme.palette.primary.main })}
                                  sortDirection={parameters.orderBy === 'year' ? parameters.order : false}
                                >
                                  <TableSortLabel
                                    active={parameters.orderBy === 'year'}
                                    direction={parameters.orderBy === 'year' ? parameters.order : undefined}
                                    onClick={() => {
                                      setParameters({
                                        ...parameters,
                                        order: parameters.orderBy === 'year' && parameters.order === 'asc' ? 'desc' : 'asc',
                                        orderBy: 'year'
                                      })
                                    }}
                                  >
                                    Year
                                  </TableSortLabel>
                                </TableCell>

                                <TableCell
                                  sx={theme => ({ color: theme.palette.primary.main })}
                                  sortDirection={parameters.orderBy === 'make' ? parameters.order : false}
                                >
                                  <TableSortLabel
                                    active={parameters.orderBy === 'make'}
                                    direction={parameters.orderBy === 'make' ? parameters.order : undefined}
                                    onClick={() => {
                                      setParameters({
                                        ...parameters,
                                        order: parameters.orderBy === 'make' && parameters.order === 'asc' ? 'desc' : 'asc',
                                        orderBy: 'make'
                                      })
                                    }}
                                  >
                                    Make
                                  </TableSortLabel>
                                </TableCell>

                                <TableCell
                                  sx={theme => ({ color: theme.palette.primary.main })}
                                  sortDirection={parameters.orderBy === 'model' ? parameters.order : false}
                                >
                                  <TableSortLabel
                                    active={parameters.orderBy === 'model'}
                                    direction={parameters.orderBy === 'model' ? parameters.order : undefined}
                                    onClick={() => {
                                      setParameters({
                                        ...parameters,
                                        order: parameters.orderBy === 'model' && parameters.order === 'asc' ? 'desc' : 'asc',
                                        orderBy: 'model'
                                      })
                                    }}
                                  >
                                    Model
                                  </TableSortLabel>
                                </TableCell>

                                <TableCell
                                  sx={theme => ({ color: theme.palette.primary.main })}
                                  sortDirection={parameters.orderBy === 'importerStockNumber' ? parameters.order : false}
                                >
                                  <TableSortLabel
                                    active={parameters.orderBy === 'importerStockNumber'}
                                    direction={parameters.orderBy === 'importerStockNumber' ? parameters.order : undefined}
                                    onClick={() => {
                                      setParameters({
                                        ...parameters,
                                        order:
                                          parameters.orderBy === 'importerStockNumber' && parameters.order === 'asc'
                                            ? 'desc'
                                            : 'asc',
                                        orderBy: 'importerStockNumber'
                                      })
                                    }}
                                  >
                                    Importer Stock#
                                  </TableSortLabel>
                                </TableCell>
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {vehicles?.map((vehicle, index) => (
                                <TableRow key={vehicle.vin} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                                  <TableCell sx={{ fontSize: '7.99pt' }}>
                                    <Checkbox
                                      color='primary'
                                      checked={values.some(sv => sv.vehicleGuid === vehicle.vehicleGuid)}
                                      disabled={isLoading}
                                      onChange={() => {
                                        setValues(prev =>
                                          values.some(sv => sv.vehicleGuid === vehicle.vehicleGuid)
                                            ? prev.filter(sv => sv.vehicleGuid !== vehicle.vehicleGuid)
                                            : [...prev, vehicle]
                                        )
                                      }}
                                    />
                                  </TableCell>

                                  <TableCell>{vehicle.vin}</TableCell>

                                  <TableCell>{vehicle.year}</TableCell>

                                  <TableCell>{vehicle.make}</TableCell>

                                  <TableCell>{vehicle.model}</TableCell>

                                  <TableCell>{vehicle.importerStockNumber}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>

                            <TableFooter>
                              <TableRow>
                                <TablePagination
                                  rowsPerPageOptions={[5, 10, 25]}
                                  SelectProps={{ fullWidth: false }}
                                  count={totalCount}
                                  rowsPerPage={parameters.pageSize}
                                  page={parameters.page}
                                  onPageChange={(_, page) => {
                                    setParameters({
                                      ...parameters,
                                      page
                                    })
                                  }}
                                  onRowsPerPageChange={e => {
                                    setParameters({
                                      ...parameters,
                                      page: 0,
                                      pageSize: e.target.value as unknown as number
                                    })
                                  }}
                                />
                              </TableRow>
                            </TableFooter>
                          </Table>
                        </TableContainer>
                      </Card>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**Cluster Shipper Number:**

Cluster Shipper # is a read only label that’s value is auto populated on save when Adding a Cluster Shipper

**Cluster Conversion Company:**

A read-only label that displays the value selected in the “Add Cluster Shipper” dialog

**Exporter:**

A read-only label that displays the value selected in the “Add Cluster Shipper” dialog

**Buyer:**

A read-only label that displays the value selected in “Add Cluster Shipper” dialog

**Select Box:**

Clicking the checkbox selects the vehicle in that row.

**CANCEL Button:**

Clicking the CANCEL button will close the dialog and not save any changes

**SAVE Button:**

Clicking this button saves any changes and adds the selected vehicles to the Cluster Shipper`}
                    </GreenText>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button variant='text' color='inherit' disabled={isLoading} onClick={onClose}>
                  CANCEL
                </Button>

                <LoadingButton loading={isLoading} type='submit' variant='contained'>
                  SAVE
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
